import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { medicationsApi } from '../../services/medicationsService'
import type { 
  Medication,
  MedicationsQueryParams 
} from '../../types/api'

// Async thunks
export const fetchMedications = createAsyncThunk(
  'medications/fetchMedications',
  async (params?: MedicationsQueryParams) => {
    const response = await medicationsApi.getMedications(params)
    return response
  }
)

// State interface
interface MedicationsState {
  medications: Medication[]
  loading: boolean
  error: string | null
  searchQuery: string
  categoryFilter: string | null
}

// Initial state
const initialState: MedicationsState = {
  medications: [],
  loading: false,
  error: null,
  searchQuery: '',
  categoryFilter: null,
}

// Slice
const medicationsSlice = createSlice({
  name: 'medications',
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload
    },
    setCategoryFilter: (state, action) => {
      state.categoryFilter = action.payload
    },
    clearFilters: (state) => {
      state.searchQuery = ''
      state.categoryFilter = null
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch medications
    builder
      .addCase(fetchMedications.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMedications.fulfilled, (state, action) => {
        state.loading = false
        state.medications = action.payload
      })
      .addCase(fetchMedications.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch medications'
      })
  },
})

// Export actions
export const { setSearchQuery, setCategoryFilter, clearFilters, clearError } = medicationsSlice.actions

// Selectors
export const selectMedications = (state: { medications: MedicationsState }) => state.medications.medications
export const selectMedicationsLoading = (state: { medications: MedicationsState }) => state.medications.loading
export const selectMedicationsError = (state: { medications: MedicationsState }) => state.medications.error
export const selectMedicationsSearchQuery = (state: { medications: MedicationsState }) => state.medications.searchQuery
export const selectMedicationsCategoryFilter = (state: { medications: MedicationsState }) => state.medications.categoryFilter

// Filtered medications selector
export const selectFilteredMedications = (state: { medications: MedicationsState }) => {
  const { medications, searchQuery, categoryFilter } = state.medications
  
  return medications.filter(medication => {
    const matchesSearch = !searchQuery || 
      medication.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      medication.category.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesCategory = !categoryFilter || medication.category === categoryFilter
    
    return matchesSearch && matchesCategory
  })
}

export default medicationsSlice.reducer